import { Controller } from "@hotwired/stimulus";
import React from 'react';
import { createRoot } from 'react-dom/client';
import ChatKitComponent from '../components/ChatKitComponent.jsx';

export default class extends Controller {
  static targets = ["container"];

  static values = { domainKey: String };

  connect() {
    // Don't mount immediately - wait for panel to open
    this.isMounted = false;
    this.setupLazyMount();

    // Check if panel is already open (e.g., after Turbo navigation with persist)
    this.checkInitialState();
  }

  checkInitialState() {
    // Check if the panel is already open by looking at the parent element's data attribute
    const panelElement = this.element.closest('[data-chatkit-open]');
    if (panelElement && panelElement.dataset.chatkitOpen === 'true') {
      // Panel is already open, mount immediately
      this.mountReactComponent();
      return;
    }

    // Fallback: Check sessionStorage for persisted state (in case Alpine hasn't set attribute yet)
    try {
      const persisted = sessionStorage.getItem('chatkitPanel.open');
      if (persisted) {
        const value = JSON.parse(persisted);
        if (value === true) {
          // Panel should be open, wait for Alpine to initialize, then mount
          if (typeof globalThis.Alpine !== 'undefined') {
            globalThis.Alpine.nextTick(() => {
              if (!this.isMounted) {
                this.mountReactComponent();
              }
            });
          } else {
            // Alpine not ready yet, use setTimeout as fallback
            setTimeout(() => {
              if (!this.isMounted) {
                this.mountReactComponent();
              }
            }, 100);
          }
        }
      }
    } catch {
      // sessionStorage might not be available
    }
  }

  disconnect() {
    this.cleanup();
    this.unmountReactComponent();
  }

  setupLazyMount() {
    // Listen for panel state changes
    this.stateChangeHandler = (event) => {
      if (event.detail.open && !this.isMounted) {
        this.mountReactComponent();
      }
    };
    globalThis.addEventListener('chatkit-panel-state-change', this.stateChangeHandler);
  }

  cleanup() {
    if (this.stateChangeHandler) {
      globalThis.removeEventListener('chatkit-panel-state-change', this.stateChangeHandler);
      this.stateChangeHandler = null;
    }
  }

  mountReactComponent() {
    if (!this.hasContainerTarget) {
      console.error('[ChatKit] No container target found');
      return;
    }

    if (this.isMounted) {
      return; // Already mounted
    }

    this.reactRoot = createRoot(this.containerTarget);

    this.reactRoot.render(
      React.createElement(ChatKitComponent, {
        domainKey: this.domainKeyValue
      })
    );

    this.isMounted = true;
  }

  unmountReactComponent() {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
      this.isMounted = false;
    }
  }
}
